import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { authErrorKey, forgotPassword } from "@/auth/cognito/client";

import { AuthLayout } from "./AuthLayout";
import { Field, FormError, SubmitButton } from "./fields";

/**
 * Step one of a password reset: ask Cognito to email a code.
 *
 * <p>The pool runs with {@code preventUserExistenceErrors}, so an unknown
 * address resolves exactly like a known one. That's the point — this form must
 * not be usable to find out who has an account — and it's why the copy on the
 * next screen says a code was sent "if that address is registered".</p>
 */
export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [email, setEmail] = useState(params.get("email") ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await forgotPassword(email);
      navigate(`/forgot/reset?email=${encodeURIComponent(email.trim())}`);
    } catch (err) {
      setError(t(authErrorKey((err as { code?: string }).code ?? "UnknownError")));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title={t("auth.forgot.title")}
      subtitle={t("auth.forgot.subtitle")}
      footer={
        <Link to="/signin" className="font-medium text-teal-700 hover:text-teal-800">
          {t("auth.backToSignIn")}
        </Link>
      }
    >
      <form className="space-y-4" onSubmit={onSubmit}>
        <Field
          label={t("auth.fields.email")}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          autoFocus
          required
        />
        <FormError>{error}</FormError>
        <SubmitButton busy={busy}>{t("auth.forgot.submit")}</SubmitButton>
      </form>
    </AuthLayout>
  );
}
